import { useQueries } from '@tanstack/react-query';

export interface ApiCallConfig<T = any> {
  queryKey: string[];
  queryFn: () => Promise<T>;
  enabled?: boolean;
  retry?: number;
  staleTime?: number;
}

export interface DashboardDataResult<T = any> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface UseDashboardDataResult {
  data: Record<string, any>;
  isLoading: boolean;
  isError: boolean;
  errors: (Error | null)[];
  refetchAll: () => void;
  refetchByKey: (key: string) => void;
}

/**
 * A hook that orchestrates multiple parallel API calls for dashboard data.
 * Centralizes loading and error states across all calls.
 *
 * @param apiCalls - Array of API call configurations
 * @returns Object with combined data, loading states, and refetch functions
 */
export const useDashboardData = (apiCalls: ApiCallConfig[]): UseDashboardDataResult => {
  // Use useQueries to run all API calls in parallel
  const queryResults = useQueries({
    queries: apiCalls.map(call => ({
      queryKey: call.queryKey,
      queryFn: call.queryFn,
      enabled: call.enabled ?? true,
      retry: call.retry ?? 1,
      staleTime: call.staleTime ?? 0,
    }))
  });

  // Combine all data into a single object keyed by the first element of queryKey
  const data = queryResults.reduce((acc, result, index) => {
    const key = apiCalls[index].queryKey[0];
    acc[key] = result.data;
    return acc;
  }, {} as Record<string, any>);

  // Combined loading state - true if any query is loading
  const isLoading = queryResults.some(result => result.isLoading);

  // Combined error state - true if any query has an error
  const isError = queryResults.some(result => result.isError);

  // Collect all errors
  const errors = queryResults.map(result => result.error);

  // Refetch all queries
  const refetchAll = () => {
    queryResults.forEach(result => result.refetch());
  };

  // Refetch specific query by key
  const refetchByKey = (key: string) => {
    const index = apiCalls.findIndex(call => call.queryKey[0] === key);
    if (index !== -1) {
      queryResults[index].refetch();
    }
  };

  return {
    data,
    isLoading,
    isError,
    errors,
    refetchAll,
    refetchByKey,
  };
};

/**
 * Helper function to create API call configurations
 */
export const createApiCall = <T = any>(
  key: string,
  queryFn: () => Promise<T>,
  options: Partial<Omit<ApiCallConfig<T>, 'queryKey' | 'queryFn'>> = {}
): ApiCallConfig<T> => ({
  queryKey: [key],
  queryFn,
  ...options,
});